import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../contexts/ToastContext';
import { createPaymentOrder, verifyPayment, validateCoupon } from '../../api/client';
import { trackPaymentFunnel } from '../../hooks/useTracking';
import { trackAbandonedCheckout } from '../../api/client';
import { buildTrackingPayload } from '../../utils/utm';
import { PAYMENT_CONFIG } from '../../utils/constants';
import Button from '../../components/ui/Button';
import Card, { CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import {
  Shield,
  Lock,
  CreditCard,
  CheckCircle,
  ArrowLeft,
  Sparkles,
  IndianRupee,
  Zap,
  Tag,
  Percent,
  X,
  Loader2,
} from 'lucide-react';

export default function PaymentScreen(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { course, amount: initialAmount, coupon_applied } = location.state || {};

  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState<any>(coupon_applied || null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [amount, setAmount] = useState<number>(initialAmount ?? course?.price ?? 0);

  useEffect(() => {
    if (document.getElementById('razorpay-script')) return;
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    if (course) {
      trackPaymentFunnel('initiated', {
        courseId: course.id,
        amount: initialAmount ?? course.price ?? 0,
      });
    }
  }, []);

  const baseAmount = (course?.discount_price ?? 0) > 0 && (course?.discount_price ?? 0) < (course?.price ?? 0)
    ? course.discount_price
    : course?.price ?? 0;

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const res = await validateCoupon(couponCode.trim(), course?.id);
      if (res.data?.valid) {
        const coupon = res.data.coupon;
        setCouponData(coupon);
        let newAmount = baseAmount;
        if (coupon.discount_type === 'percentage') {
          newAmount = baseAmount - (baseAmount * Number(coupon.discount_value) / 100);
        } else {
          newAmount = baseAmount - Number(coupon.discount_value);
        }
        newAmount = Math.max(newAmount, 0);
        setAmount(Math.round(newAmount * 100) / 100);
        toast.show(`Coupon applied! You save ₹${Math.round((baseAmount - newAmount) * 100) / 100}`, 'success');
      } else {
        setCouponData(null);
        setAmount(baseAmount);
        toast.show(res.data?.message || 'Invalid coupon', 'error');
      }
    } catch (err: any) {
      toast.show(err.message || 'Failed to validate coupon', 'error');
      setCouponData(null);
      setAmount(baseAmount);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponData(null);
    setAmount(baseAmount);
  };

  const handlePayment = async () => {
    try {
      const orderRes = await createPaymentOrder(course.id, couponData?.code || couponCode || undefined);
      trackPaymentFunnel('started', {
        courseId: course.id,
        amount: orderRes.amount / 100,
      });

      if (orderRes.free) {
        toast.show('Enrolled successfully!', 'success');
        navigate('/');
        return;
      }

      const options = {
        key: orderRes.key,
        amount: orderRes.amount,
        currency: orderRes.currency,
        name: orderRes.name,
        description: orderRes.description,
        order_id: orderRes.order_id,
        prefill: orderRes.prefill,
        theme: { color: PAYMENT_CONFIG.THEME_COLOR },
        handler: async function (response: any) {
          try {
            await verifyPayment({
              razorpay_order_id: orderRes.order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            trackPaymentFunnel('success', {
              courseId: course.id,
              amount: orderRes.amount / 100,
              orderId: orderRes.order_id,
            });
            toast.show('Payment successful! Welcome to the course.', 'success');
            navigate('/');
          } catch (err: any) {
            trackPaymentFunnel('failed', {
              courseId: course.id,
              amount: orderRes.amount / 100,
              reason: err.message || 'verification_failed',
            });
            toast.show(err.message || 'Payment verification failed', 'error');
          }
        },
        modal: {
          ondismiss: function () {
            trackPaymentFunnel('abandoned', {
              courseId: course.id,
              amount: orderRes.amount / 100,
            });
            // Track abandoned checkout for recovery
            trackAbandonedCheckout({
              course_id: course.id,
              email: orderRes.prefill?.email ?? '',
              phone: orderRes.prefill?.contact ?? '',
              amount: orderRes.amount / 100,
              cart_data: { course_title: course.title, coupon: couponData?.code || couponCode || '' },
              ...buildTrackingPayload(),
            }).catch(() => {});
            toast.show('Payment cancelled', 'error');
          },
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      toast.show(error.message || 'Failed to initiate payment', 'error');
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">No payment details</h2>
          <p className="text-slate-500 mb-4">Please select a course to enroll first.</p>
          <Button onClick={() => navigate('/')} leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Browse Courses
          </Button>
        </div>
      </div>
    );
  }

  const discountAmount = course.discount_price > 0 && course.discount_price < course.price
    ? course.price - course.discount_price
    : 0;
  const couponDiscount = couponData
    ? Math.round((baseAmount - amount) * 100) / 100
    : 0;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50/50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Complete Your Enrollment</h1>
          <p className="text-slate-500 text-sm">Secure payment powered by Razorpay</p>
        </div>

        <Card className="overflow-hidden">
          {/* Course summary */}
          <div className="bg-amber-50 p-6 border-b border-amber-100">
            <div className="flex items-start gap-4">
              {course.course_image ? (
                <img
                  src={course.course_image}
                  alt={course.title}
                  className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-8 h-8 text-amber-500" />
                </div>
              )}
              <div className="min-w-0">
                <h3 className="font-bold text-slate-900 text-sm line-clamp-2">{course.title}</h3>
                {course.trainer_name && (
                  <p className="text-xs text-slate-500 mt-1">by {course.trainer_name}</p>
                )}
                {course.duration && (
                  <Badge variant="info" size="sm" className="mt-2">
                    {course.duration}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <CardContent className="space-y-4">
            {/* Coupon */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Have a coupon?</label>
              <AnimatePresence mode="wait">
                {couponData ? (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl"
                  >
                    <Tag className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700 flex-1">
                      {couponData.code} — {couponData.discount_type === 'percentage' ? `${couponData.discount_value}%` : `₹${couponData.discount_value}`} off
                    </span>
                    <button type="button" onClick={removeCoupon} className="text-green-600 hover:text-green-800">
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="flex gap-2"
                  >
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleValidateCoupon()}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      isLoading={validatingCoupon}
                      onClick={handleValidateCoupon}
                      leftIcon={<Percent className="w-3.5 h-3.5" />}
                    >
                      Apply
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Price breakdown */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Course price</span>
                <span className="text-slate-900 font-medium">₹{Number(course.price).toLocaleString()}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-600 font-medium">Course discount</span>
                  <span className="text-green-600 font-medium">-₹{discountAmount.toLocaleString()}</span>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-500 font-medium">Coupon discount</span>
                  <span className="text-amber-500 font-medium">-₹{couponDiscount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <span className="text-slate-900 font-bold">Total</span>
                <span className="text-2xl font-bold text-slate-900">₹{Number(amount).toLocaleString()}</span>
              </div>
            </div>

            {/* Security badges */}
            <div className="flex items-center justify-center gap-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Shield className="w-4 h-4 text-green-500" />
                SSL Secure
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Lock className="w-4 h-4 text-green-500" />
                Encrypted
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Verified
              </div>
            </div>

            {/* Pay button */}
            <Button onClick={handlePayment} size="lg" className="w-full" leftIcon={<IndianRupee className="w-4 h-4" />}>
              Pay ₹{Number(amount).toLocaleString()}
            </Button>

            <button
              onClick={() => navigate(-1)}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors py-2"
            >
              Go back
            </button>
          </CardContent>
        </Card>

        {/* Trust signals */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">
            30-day money-back guarantee · Instant access · Lifetime updates
          </p>
        </div>
      </motion.div>
    </div>
  );
}
